import { motion } from "framer-motion";
import { useContext, useState } from "react";
import api from "../../api/api";
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import { routes, getDefaultRouteForRoles } from "./../../config/utility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import ThemeButton from "../../components/atoms/ThemeButton";
import SecondaryThemeButton from "../../components/atoms/SecondaryThemeButton";

const Login = () => {
  const { contextLogin, updateContext } = useContext(UserContext);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/login", { login: identifier, password });
      contextLogin(res.data);
      updateContext();
      navigate(getDefaultRouteForRoles(res.data.user?.role ?? []));
    } catch (err) {
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <motion.div
      className="flex h-screen w-full overflow-hidden bg-body-auth"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-50 bg-white/90 hover:bg-white text-gray-700 hover:text-primary 
                   rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 
                   backdrop-blur-md border border-white/20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
      </motion.button>

      {/* Main Container */}
      <div className="w-full flex items-center justify-center relative backdrop-blur-lg px-4 md:p-0">
        {/* Right Image Panel - Hide on mobile */}
        <motion.div
          className="hidden md:block absolute -right-5 top-0 h-full w-1/3 z-10"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: "spring", duration: 0.8 }}
        >
          <img
            src="/assets/login-illustration.png"
            alt=""
            className="h-full object-cover object-left"
          />
        </motion.div>

        {/* Sign-in Card */}
        <motion.div
          className="flex flex-col justify-center relative w-full 
                     max-w-[90%] sm:max-w-md md:max-w-lg lg:max-w-[47%] mx-auto 
                     bg-white/90 border border-white/20 md:h-[calc(100vh-150px)] 
                     rounded-xl md:rounded-2xl p-5 sm:p-8 md:p-10 lg:p-12 
                     backdrop-blur-md shadow-xl z-20"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div
            className="text-center md:text-start mb-4 md:mb-8"
            variants={itemVariants}
          >
            <motion.img
              src="/assets/FULLLOGO.svg"
              alt="Popout"
              className="w-32 sm:w-40 md:w-64 mb-4 md:mb-8 mx-auto md:mx-0"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            />
            <motion.h2
              className="text-lg sm:text-xl md:text-2xl font-display font-bold text-dark"
              variants={itemVariants}
            >
              Sign&nbsp;In
            </motion.h2>
          </motion.div>

          <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4 md:space-y-6">
            {/* identifier */}
            <motion.div variants={itemVariants}>
              <label htmlFor="identifier" className="block text-xs sm:text-sm mb-1.5 text-dark">Login&nbsp;ID</label>
              <input
                id="identifier"
                type="text"
                placeholder="Email, Username, or Phone"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="w-full text-sm sm:text-base"
              />
            </motion.div>

            {/* password */}
            <motion.div variants={itemVariants}>
              <label htmlFor="password" className="block text-xs sm:text-sm mb-1.5 text-dark">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full text-sm sm:text-base pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-primary transition-colors z-10"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* remember / forgot */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-start sm:items-center 
                       justify-between text-xs sm:text-sm text-grey gap-4 sm:gap-0 pt-1"
            >
              <label className="inline-flex items-center">
                <input type="checkbox"
                  className="form-checkbox h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary bg-transparent border-ash" />
                <span className="ml-2">Remember me</span>
              </label>

              <SecondaryThemeButton
                label="Forgot password?"
                className="text-xs sm:text-sm sm:ml-0"
                onClick={() => navigate(routes.forgot_password)}
              />
            </motion.div>

            {/* submit */}
            <motion.div
              variants={itemVariants}
              className="flex justify-center items-center w-full pt-2 md:pt-4"
            >
              <ThemeButton
                type="submit"
                label={loading ? "Please wait..." : "Continue"}
                onClick={handleLogin}
                disabled={loading}
                className="w-full sm:max-w-xs text-sm sm:text-base py-2.5 sm:py-3"
                primaryColor="bg-primary"
                secondaryColor="bg-accentSecondary"
                textColor="#ffffff"
                hoverTextColor="#160033"
              />
            </motion.div>
          </form>

          {/* register */}
          <motion.p
            variants={itemVariants}
            className="mt-4 md:mt-8 text-xs sm:text-sm text-center text-grey"
          >
            Don't have an account?{" "}
            <SecondaryThemeButton
              label="Create one"
              className="text-sm sm:text-base md:text-lg"
              onClick={() => navigate(routes.registration)}
            />
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Login;